import { useState } from "react";
import { LuDownload, LuLoaderCircle, LuTriangleAlert } from "react-icons/lu";
import { BuildJobStatusSchema } from "../../../../darvin-shared/schema/zod/build";
import { auth } from "../../../config/firebase_config";
import {
  useProjectExportsActions,
  useProjectLatestCompleteExport,
  useProjectLatestExport,
} from "../../../core_stores/useProjectExportsStore";
import { colors, components, typography } from "../../../design/tokens";
import { HttpFailedToFetchException } from "../../../model/httpException";
import { useSnackbar } from "../../../providers/SnackbarProvider";
import { getAuthHeaders } from "../../../utils/authHeaders";
import { formatDateToLocal } from "../../../utils/dateFormatter";
import { logException } from "../../../utils/logUtils";
import { fetchWithRetry } from "../../../utils/retry.util";

export default function ProjectExportData() {
  return (
    <div className="flex-1 flex flex-col gap-4 overflow-hidden">
      <div
        className={`flex items-center justify-between ${typography.size.sm}`}
      >
        <span className="font-medium">Exported project</span>
        <span
          className="mr-2 text-red-500"
          data-tooltip-id="dashboard-tooltip"
          data-tooltip-html={
            "Heads up: This exported project includes sensitive details, like the key and passphrase used to publish your app on Google Play. Please keep it in a safe place and don't share it with anyone you don't trust."
          }
        >
          <LuTriangleAlert />
        </span>
      </div>
      <LatestExport />
      <LatestCompleteExport />
    </div>
  );
}

function LatestExport() {
  const latestExport = useProjectLatestExport();

  if (
    !latestExport ||
    latestExport.status === BuildJobStatusSchema.enum.complete
  ) {
    return null;
  }

  return (
    <div className="mb-4 overflow-hidden rounded-lg border border-dark-border">
      <div
        className={`px-4 flex items-center justify-between gap-3 h-[50px] cursor-pointer ${typography.size.sm}`}
      >
        <span className="text-center">Export</span>
        <div
          className={`flex items-center gap-2
            ${colors.text.placeholder}`}
        >
          <span className="flex items-center gap-1">
            {latestExport!.status.charAt(0).toUpperCase() +
              latestExport!.status.slice(1)}
          </span>
        </div>
        <span className={`text-center ${colors.text.placeholder}`}>
          {formatDateToLocal(latestExport!.lastUpdateTimestamp)}
        </span>
      </div>

      {(latestExport.status === BuildJobStatusSchema.enum.error ||
        latestExport.status === BuildJobStatusSchema.enum.cancelled) && (
        <div
          className={`overflow-hidden max-h-[500px] p-4
      flex items-center justify-center
      ${colors.text.placeholder} ${typography.size.sm}`}
        >
          Something went wrong, try again later.
        </div>
      )}
    </div>
  );
}

function LatestCompleteExport() {
  const latestCompleteExport = useProjectLatestCompleteExport();

  const { loadExportUrl } = useProjectExportsActions();
  const { showSnackbar } = useSnackbar();

  const [isDownloading, setIsDownloading] = useState(false);

  const downloadZip = async () => {
    setIsDownloading(true);
    const exportUrl = await loadExportUrl();

    if (!exportUrl) {
      showSnackbar("No export URL found. Please try again later.", {
        type: "error",
      });
      return;
    }

    try {
      const user = auth.currentUser;
      const authToken = await user?.getIdToken();

      if (!authToken) {
        showSnackbar("You must be logged in to download files.", {
          type: "error",
        });
        return;
      }

      let urlToFetch = exportUrl;
      if (import.meta.env.DEV) {
        urlToFetch = exportUrl.replace(
          "https://api-gateway-15020826245.us-central1.run.app/v1",
          "/v1"
        );
      }

      // Fetch the file with authorization headers
      const headers = await getAuthHeaders();
      const response = await fetchWithRetry(urlToFetch, {
        method: "GET",
        headers,
      });

      if (!response.ok) {
        const errorText = await response.text();
        logException("Download API error:", {
          status: response.status,
          statusText: response.statusText,
          body: errorText,
        });
        throw new HttpFailedToFetchException();
      }

      // Convert response to blob
      const blob = await response.blob();

      // Create download link
      const link = document.createElement("a");
      link.setAttribute("download", "export.zip");

      // Create object URL from blob
      const objectUrl = URL.createObjectURL(blob);
      link.href = objectUrl;

      // Trigger download
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up object URL
      URL.revokeObjectURL(objectUrl);
    } catch (error) {
      console.error("Error downloading file:", error);
      showSnackbar("Failed to download file. Please try again.", {
        type: "error",
      });
    } finally {
      setIsDownloading(false);
    }
  };

  if (!latestCompleteExport) {
    return null;
  }

  return (
    <div className="mb-4 overflow-hidden rounded-lg border border-dark-border">
      <div
        className={`px-4 flex items-center justify-between gap-3 h-[50px] cursor-pointer ${typography.size.sm}`}
      >
        <span className="text-center">Export</span>
        <div
          className={`flex items-center gap-2
            ${colors.text.placeholder}`}
        >
          <span className="flex items-center gap-1">
            {latestCompleteExport!.status.charAt(0).toUpperCase() +
              latestCompleteExport!.status.slice(1)}
          </span>
        </div>
        <span className={`text-center ${colors.text.placeholder}`}>
          {formatDateToLocal(latestCompleteExport!.lastUpdateTimestamp)}
        </span>
      </div>

      <div className="overflow-hidden transition-all duration-500 ease-in-out max-h-[500px] opacity-100 mt-0">
        <div
          className={`px-4 py-3 ${colors.overlay.light}
            transform transition-transform duration-300 ease-in-out translate-y-0`}
        >
          <div className="flex flex-col items-center justify-center gap-4">
            <button
              id="download-project-export"
              className={`w-full max-w-[300px] ${components.button.primary}`}
              onClick={downloadZip}
              disabled={isDownloading}
            >
              {isDownloading ? (
                <LuLoaderCircle className="animate-spin" size={16} />
              ) : (
                <>
                  <span className="underline">Download</span>
                  <LuDownload size={16} />
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
